// @seventide/types — workspace module DTOs (FR-1.3–FR-1.7, R-3.1–R-3.4, R-5.2, R-5.3).
// Owned by the workspace module (docs/ARCHITECTURE.md §6). Shared by the API (validation) and the web (forms).
import { z } from 'zod';
import { Id, IsoDate, IsoDateTime, paginated, PaginationQuery } from './common';
import { HealthErasure } from './health';
import {
  AppModule,
  AttorneyActing,
  CareSetting,
  ConsentType,
  EntityType,
  HomeOwnership,
  LegalInstrumentType,
  MaritalStatus,
  ModuleVisibility,
  Nation,
  Role,
  Stage,
  type VisibilityModule,
} from './enums';

// =================================================================== roles, members & invites (FR-1.3, R-3.x)

/** Roles that can be invited or assigned. Ownership only moves through the transfer flow (R-3.1). */
export const ASSIGNABLE_ROLES = ['attorney', 'family', 'principal', 'professional'] as const;
export const AssignableRole = z.enum(ASSIGNABLE_ROLES);
export type AssignableRole = z.infer<typeof AssignableRole>;

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Sets up the workspace, manages members and billing, and can see and change everything.',
  attorney:
    'Acts under a registered lasting power of attorney or deputyship. Can see and record everything except billing once the registered document is uploaded.',
  family: 'Can see the areas you choose, add care diary notes and documents, and comment.',
  principal: 'The person this workspace is about. Can always see everything about themselves and give consent.',
  professional: 'An adviser or solicitor. Can see the areas you choose and comment, but not change records.',
};

const NONE_VISIBLE: ModuleVisibility = {
  gifts: false,
  money: false,
  care: false,
  health: false,
  attorney: false,
  documents: false,
};

/**
 * Module visibility pre-set when inviting (R-3.3). Data minimisation: only what most families share with
 * that role; the inviter can change it before sending and the owner can change it at any time.
 */
export function defaultVisibilityFor(role: Role): ModuleVisibility {
  if (role === 'family') return { ...NONE_VISIBLE, care: true, health: true, documents: true };
  if (role === 'professional') return { ...NONE_VISIBLE, care: true, documents: true };
  return { ...NONE_VISIBLE };
}

/** Roles whose visibility is configured per member (R-3.3). Others always see everything (R-3.4). */
export const roleHasConfigurableVisibility = (role: Role) => role === 'family' || role === 'professional';

/** FR-13.1: the Family plan covers up to 5 members plus 1 Principal. */
export const MAX_MEMBERS = 5;

export const MemberDto = z.object({
  /** Membership id. */
  id: Id,
  userId: Id,
  name: z.string().nullable(),
  /** Only shown to people who manage members, and to the member themselves. */
  email: z.string().nullable(),
  role: Role,
  /** Stored per-module visibility (meaningful for family and professional members). */
  moduleVisibility: ModuleVisibility,
  /** What they can actually see (owner/attorney/principal see everything, R-3.4). */
  effectiveVisibility: ModuleVisibility,
  /** R-3.2: attorney with restricted rights until their registered LPA / deputyship is uploaded. */
  lpaPending: z.boolean(),
  /** Attorneys: named on a legal instrument that has the registered document linked. */
  namedOnInstrument: z.boolean(),
  isYou: z.boolean(),
  joinedAt: IsoDateTime.nullable(),
});
export type MemberDto = z.infer<typeof MemberDto>;

export const INVITE_STATUSES = ['pending', 'expired', 'accepted', 'revoked'] as const;
/** SR-26 anti-abuse caps on invite EMAILS (creates + resends; link invites send no email), rolling 24 hours. */
export const INVITE_EMAILS_PER_USER_PER_DAY = 50; // one person, across all their workspaces
export const INVITE_EMAILS_PER_WORKSPACE_PER_DAY = 25; // a workspace has at most MAX_MEMBERS seats
/** SR-26: an emailed invite can be sent again at most once per this many minutes. */
export const INVITE_RESEND_COOLDOWN_MINUTES = 10;
export const InviteStatus = z.enum(INVITE_STATUSES);
export type InviteStatus = z.infer<typeof InviteStatus>;
export const InviteChannel = z.enum(['email', 'link']);
export type InviteChannel = z.infer<typeof InviteChannel>;

export const InviteDto = z.object({
  id: Id,
  channel: InviteChannel,
  email: z.string().nullable(),
  role: AssignableRole,
  moduleVisibility: ModuleVisibility,
  status: InviteStatus,
  expiresAt: IsoDateTime,
  createdAt: IsoDateTime,
  lastSentAt: IsoDateTime.nullable(),
  invitedByName: z.string().nullable(),
});
export type InviteDto = z.infer<typeof InviteDto>;

export const MembersResponse = z.object({
  members: z.array(MemberDto),
  /** Pending and recently expired invites — only for people who can invite. */
  invites: z.array(InviteDto),
  /** Owner only: change roles, visibility, remove members, transfer ownership. */
  canManage: z.boolean(),
  /** Owner and attorneys with an uploaded LPA. */
  canInvite: z.boolean(),
  maxMembers: z.number().int(),
  /** Members plus pending invites, not counting the Principal. */
  seatsUsed: z.number().int(),
  inviteExpiryDays: z.number().int(),
});
export type MembersResponse = z.infer<typeof MembersResponse>;

export const CreateInviteRequest = z
  .object({
    channel: InviteChannel,
    email: z.string().trim().toLowerCase().email('Enter an email address like name@example.com').max(254).optional(),
    role: AssignableRole,
    /** Family and professional only; defaults to `defaultVisibilityFor(role)`. */
    moduleVisibility: ModuleVisibility.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.channel === 'email' && !v.email)
      ctx.addIssue({ code: 'custom', path: ['email'], message: 'Enter the email address to send the invite to' });
  });
export type CreateInviteRequest = z.infer<typeof CreateInviteRequest>;

export const CreateInviteResponse = z.object({
  invite: InviteDto,
  /** The invite link. Shown once: only a hash of the token is stored. */
  link: z.string(),
  emailSent: z.boolean(),
});
export type CreateInviteResponse = z.infer<typeof CreateInviteResponse>;

export const UpdateMemberRequest = z
  .object({ role: AssignableRole.optional(), moduleVisibility: ModuleVisibility.optional() })
  .refine((v) => v.role !== undefined || v.moduleVisibility !== undefined, {
    message: 'Choose a role or what they can see',
  });
export type UpdateMemberRequest = z.infer<typeof UpdateMemberRequest>;

export const TransferOwnershipRequest = z.object({
  /** Membership id of the new owner. */
  membershipId: Id,
  /** The current owner's role afterwards. */
  myNewRole: z.enum(['attorney', 'family']),
  confirm: z.literal(true),
});
export type TransferOwnershipRequest = z.infer<typeof TransferOwnershipRequest>;

/** GET /api/invites/:workspaceId/:token — what an invitee sees before accepting. */
export const InvitePreview = z.object({
  status: InviteStatus,
  workspaceName: z.string(),
  inviterName: z.string().nullable(),
  role: AssignableRole,
  channel: InviteChannel,
  /** Masked address the invite was sent to, e.g. "s•••@example.com". */
  emailHint: z.string().nullable(),
  expiresAt: IsoDateTime,
  signedIn: z.boolean(),
  alreadyMember: z.boolean(),
  /** Email invites: whether the signed-in account matches the invited address (null when signed out / link). */
  emailMatches: z.boolean().nullable(),
  /** Signed in without a name yet: ask for it before joining (guided sign-up). */
  needsName: z.boolean(),
});
export type InvitePreview = z.infer<typeof InvitePreview>;

export const AcceptInviteRequest = z.object({
  /** Sets the account name when it is not set yet. */
  name: z.string().trim().min(1, 'Enter your name').max(120).optional(),
});
export type AcceptInviteRequest = z.infer<typeof AcceptInviteRequest>;

export const AcceptInviteResponse = z.object({ workspaceId: Id, alreadyMember: z.boolean() });
export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponse>;

// =================================================================== Principal profile (FR-1.5)

export const MARITAL_STATUS_LABELS: Record<MaritalStatus, string> = {
  single: 'Single',
  married: 'Married',
  civil_partnership: 'In a civil partnership',
  widowed: 'Widowed',
  divorced: 'Divorced',
  separated: 'Separated',
  cohabiting: 'Living with a partner (not married)',
};

export const HOME_OWNERSHIP_LABELS: Record<HomeOwnership, string> = {
  owns_outright: 'Owns their home outright',
  owns_with_mortgage: 'Owns their home with a mortgage',
  joint_owner: 'Owns their home jointly with someone else',
  rents: 'Rents their home',
  lives_with_family: 'Lives with family',
  other: 'Something else',
};

export const CARE_SETTING_LABELS: Record<CareSetting, string> = {
  own_home: 'At home, without paid care',
  home_with_care: 'At home, with paid carers',
  residential_home: 'Residential care home',
  nursing_home: 'Nursing home',
  hospital: 'Hospital',
  other: 'Somewhere else',
};

export const OCCUPANT_RELATIONSHIPS = [
  'spouse_partner',
  'child',
  'grandchild',
  'sibling',
  'parent',
  'other_relative',
  'carer',
  'friend',
  'lodger',
  'other',
] as const;
export const OccupantRelationship = z.enum(OCCUPANT_RELATIONSHIPS);
export type OccupantRelationship = z.infer<typeof OccupantRelationship>;
export const OCCUPANT_RELATIONSHIP_LABELS: Record<OccupantRelationship, string> = {
  spouse_partner: 'Husband, wife or partner',
  child: 'Son or daughter',
  grandchild: 'Grandchild',
  sibling: 'Brother or sister',
  parent: 'Parent',
  other_relative: 'Other relative',
  carer: 'Live-in carer',
  friend: 'Friend',
  lodger: 'Lodger or tenant',
  other: 'Someone else',
};
const RELATIVES: readonly OccupantRelationship[] = [
  'spouse_partner',
  'child',
  'grandchild',
  'sibling',
  'parent',
  'other_relative',
];

export const Occupant = z.object({
  name: z.string().trim().max(120).nullable().optional(),
  relationship: OccupantRelationship,
  age: z.number().int().min(0, 'Enter an age of 0 or more').max(120, 'Enter an age of 120 or less').nullable(),
  /** Has a disability (affects the care property disregard). */
  disabled: z.boolean(),
});
export type Occupant = z.infer<typeof Occupant>;

/** The flags the care means test needs (stored unencrypted on the principal for the care module). */
export interface OccupantFlags {
  spouse: boolean;
  relativeOver60: boolean;
  disabledRelative: boolean;
  childUnder18: boolean;
}

/**
 * FR-1.5 / FR-5.1: whether anyone living in the home could make it disregarded in a care means test
 * (spouse/partner, relative aged 60+, disabled relative, child under 18). The care module applies the rule.
 */
export function occupantFlags(occupants: readonly Occupant[]): OccupantFlags {
  const isRelative = (o: Occupant) => RELATIVES.includes(o.relationship);
  return {
    spouse: occupants.some((o) => o.relationship === 'spouse_partner'),
    relativeOver60: occupants.some((o) => isRelative(o) && o.age !== null && o.age >= 60),
    disabledRelative: occupants.some((o) => isRelative(o) && o.disabled),
    childUnder18: occupants.some(
      (o) => (o.relationship === 'child' || o.relationship === 'grandchild') && o.age !== null && o.age < 18,
    ),
  };
}

/** First applicable property-disregard reason (same keys as `@seventide/rules` PropertyDisregardReason). */
export function propertyDisregardReason(
  flags: OccupantFlags,
): 'spouse_partner' | 'relative_over_60' | 'disabled_relative' | 'child_under_18' | null {
  if (flags.spouse) return 'spouse_partner';
  if (flags.relativeOver60) return 'relative_over_60';
  if (flags.disabledRelative) return 'disabled_relative';
  if (flags.childUnder18) return 'child_under_18';
  return null;
}

export const Condition = z.object({
  name: z.string().trim().min(1, 'Enter the condition or diagnosis').max(200),
  diagnosedOn: IsoDate.nullable(),
  /** Progressive (e.g. dementia, Parkinson's): one of the FR-9.1 deprivation-guard triggers. */
  progressive: z.boolean(),
  notes: z.string().trim().max(1000).nullable().optional(),
});
export type Condition = z.infer<typeof Condition>;

export const CARE_ASSESSMENT_KINDS = [
  'care_needs',
  'financial',
  'chc_checklist',
  'chc_full',
  'review',
  'other',
] as const;
export const CareAssessmentKind = z.enum(CARE_ASSESSMENT_KINDS);
export type CareAssessmentKind = z.infer<typeof CareAssessmentKind>;
export const CARE_ASSESSMENT_KIND_LABELS: Record<CareAssessmentKind, string> = {
  care_needs: 'Council care needs assessment',
  financial: 'Council financial assessment',
  chc_checklist: 'NHS Continuing Healthcare checklist',
  chc_full: 'NHS Continuing Healthcare full assessment',
  review: 'Care review',
  other: 'Other assessment',
};

export const CareAssessment = z.object({
  date: IsoDate,
  kind: CareAssessmentKind,
  note: z.string().trim().max(500).nullable().optional(),
});
export type CareAssessment = z.infer<typeof CareAssessment>;

export const GpDetails = z.object({
  name: z.string().trim().max(120).nullable(),
  surgery: z.string().trim().max(200).nullable(),
  phone: z.string().trim().max(40).nullable(),
});
export type GpDetails = z.infer<typeof GpDetails>;

/** NHS number (England, Wales, Isle of Man): 10 digits, modulus-11 check digit. Spaces/hyphens allowed. */
export function normaliseNhsNumber(input: string): string | null {
  const digits = input.replace(/[\s-]/g, '');
  if (!/^\d{10}$/.test(digits)) return null;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(digits[i]) * (10 - i);
  let check = 11 - (sum % 11);
  if (check === 11) check = 0;
  if (check === 10 || check !== Number(digits[9])) return null;
  return digits;
}

/** "943 476 5919" → "••• ••• 5919" (FR-1.5 masked display). */
export function maskNhsNumber(digits: string): string {
  return `••• ••• ${digits.slice(-4)}`;
}

/** "943 476 5919" spacing. */
export function formatNhsNumber(digits: string): string {
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

export const EstateDetails = z.object({
  /** Unused nil-rate band transferred from a spouse or civil partner who died first (0–100%). */
  transferableNrbPct: z.number().min(0, 'Enter 0 to 100').max(100, 'Enter 0 to 100'),
  transferableRnrbPct: z.number().min(0, 'Enter 0 to 100').max(100, 'Enter 0 to 100'),
  /** A home (or share) passes to children or grandchildren on death (RNRB condition). */
  residenceToDescendants: z.boolean().nullable(),
});
export type EstateDetails = z.infer<typeof EstateDetails>;

export const PrincipalHealthDetails = z.object({
  gp: GpDetails.nullable(),
  nhsNumberMasked: z.string().nullable(),
  hasNhsNumber: z.boolean(),
  conditions: z.array(Condition),
  hasProgressiveCondition: z.boolean(),
  careAssessments: z.array(CareAssessment),
  lastCareAssessmentDate: IsoDate.nullable(),
  /**
   * Set while health records are hidden after the last consent covering health was withdrawn (and after they were
   * deleted): conditions, the progressive flag and care assessments are then empty. GP and NHS number stay.
   */
  withdrawn: HealthErasure.nullable(),
});
export type PrincipalHealthDetails = z.infer<typeof PrincipalHealthDetails>;

export const PrincipalProfile = z.object({
  id: Id,
  fullName: z.string(),
  dateOfBirth: IsoDate.nullable(),
  nation: Nation,
  maritalStatus: MaritalStatus.nullable(),
  homeOwnership: HomeOwnership.nullable(),
  otherOccupants: z.array(Occupant),
  occupantFlags: z.custom<OccupantFlags>(),
  careSetting: CareSetting.nullable(),
  careSettingSince: IsoDate.nullable(),
  firstCareNeedDate: IsoDate.nullable(),
  stage: Stage,
  dateOfDeath: IsoDate.nullable(),
  /** Null when the viewer cannot see gifts (R-3.3). */
  estate: EstateDetails.nullable(),
  /** Null when the viewer cannot see health evidence (R-3.3): special category data (R-5.2). */
  health: PrincipalHealthDetails.nullable(),
  canEdit: z.boolean(),
  updatedAt: IsoDateTime,
});
export type PrincipalProfile = z.infer<typeof PrincipalProfile>;

/** Stages that can be chosen on the profile; "estate" is set by recording a date of death (FR-10.1). */
export const ProfileStage = z.enum(['gifting', 'care']);

export const UpdatePrincipalRequest = z.object({
  fullName: z.string().trim().min(1, 'Enter their full name').max(120).optional(),
  dateOfBirth: IsoDate.nullable().optional(),
  nation: Nation.optional(),
  maritalStatus: MaritalStatus.nullable().optional(),
  homeOwnership: HomeOwnership.nullable().optional(),
  otherOccupants: z.array(Occupant).max(12, 'Add up to 12 people').optional(),
  careSetting: CareSetting.nullable().optional(),
  careSettingSince: IsoDate.nullable().optional(),
  firstCareNeedDate: IsoDate.nullable().optional(),
  stage: ProfileStage.optional(),
  estate: EstateDetails.partial().optional(),
  // Health details (need health visibility to change)
  gp: GpDetails.nullable().optional(),
  /** Digits with optional spaces; null removes it. */
  nhsNumber: z.string().trim().max(20).nullable().optional(),
  conditions: z.array(Condition).max(40, 'Add up to 40 conditions').optional(),
  careAssessments: z.array(CareAssessment).max(60).optional(),
});
export type UpdatePrincipalRequest = z.infer<typeof UpdatePrincipalRequest>;

export const NhsNumberResponse = z.object({ nhsNumber: z.string().nullable() });
export type NhsNumberResponse = z.infer<typeof NhsNumberResponse>;

// =================================================================== legal instruments (R-3.2)

export const LEGAL_INSTRUMENT_TYPE_LABELS: Record<LegalInstrumentType, string> = {
  lpa_pfa: 'Lasting power of attorney: property and financial affairs',
  lpa_hw: 'Lasting power of attorney: health and welfare',
  epa: 'Enduring power of attorney',
  deputyship: 'Court of Protection deputyship order',
};
export const LEGAL_INSTRUMENT_SHORT_LABELS: Record<LegalInstrumentType, string> = {
  lpa_pfa: 'LPA (property and financial affairs)',
  lpa_hw: 'LPA (health and welfare)',
  epa: 'Enduring power of attorney',
  deputyship: 'Deputyship order',
};
export const ATTORNEY_ACTING_LABELS: Record<AttorneyActing, string> = {
  jointly: 'Jointly (must make decisions together)',
  jointly_and_severally: 'Jointly and severally (together or on their own)',
  sole: 'Sole attorney or deputy',
};

export const LegalInstrumentAttorney = z.object({
  name: z.string().trim().min(1, 'Enter the attorney’s name').max(120),
  /** The workspace member this attorney is, if they have joined. */
  userId: Id.nullable().optional(),
});
export type LegalInstrumentAttorney = z.infer<typeof LegalInstrumentAttorney>;

export const LinkedDocumentRef = z.object({
  id: Id,
  title: z.string().nullable(),
  /** Short-lived signed URL (null when the viewer cannot see documents). */
  downloadUrl: z.string().nullable(),
});
export type LinkedDocumentRef = z.infer<typeof LinkedDocumentRef>;

export const LegalInstrumentDto = z.object({
  id: Id,
  type: LegalInstrumentType,
  registrationDate: IsoDate.nullable(),
  attorneys: z.array(LegalInstrumentAttorney),
  acting: AttorneyActing.nullable(),
  opgReference: z.string().nullable(),
  documentId: Id.nullable(),
  document: LinkedDocumentRef.nullable(),
  uploadedByName: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type LegalInstrumentDto = z.infer<typeof LegalInstrumentDto>;

export const SaveLegalInstrumentRequest = z
  .object({
    type: LegalInstrumentType,
    registrationDate: IsoDate.nullable(),
    attorneys: z.array(LegalInstrumentAttorney).min(1, 'Add at least one attorney or deputy').max(10),
    acting: AttorneyActing.nullable(),
    opgReference: z.string().trim().max(40).nullable().optional(),
    /** A vault document of category "LPA / deputyship" (the registered instrument). */
    documentId: Id.nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.acting === 'sole' && v.attorneys.length > 1)
      ctx.addIssue({ code: 'custom', path: ['acting'], message: 'Choose how the attorneys act together' });
    if ((v.acting === 'jointly' || v.acting === 'jointly_and_severally') && v.attorneys.length < 2)
      ctx.addIssue({ code: 'custom', path: ['acting'], message: 'Add the other attorney, or choose sole attorney' });
    const ids = v.attorneys.map((a) => a.userId).filter(Boolean);
    if (new Set(ids).size !== ids.length)
      ctx.addIssue({ code: 'custom', path: ['attorneys'], message: 'Each member can only be linked once' });
  });
export type SaveLegalInstrumentRequest = z.infer<typeof SaveLegalInstrumentRequest>;

// =================================================================== consent (FR-1.4, R-5.2, R-5.3)

export const CONSENT_METHODS = [
  'in_app_signature',
  'paper_upload',
  'witnessed_declaration',
  'attorney_declaration',
  'pending',
] as const;
export const ConsentMethod = z.enum(CONSENT_METHODS);
export type ConsentMethod = z.infer<typeof ConsentMethod>;
export const CONSENT_METHOD_LABELS: Record<ConsentMethod, string> = {
  in_app_signature: 'Signed in the app',
  paper_upload: 'Signed on paper (photo uploaded)',
  witnessed_declaration: 'Witnessed paper declaration',
  attorney_declaration: 'Attorney or deputy declaration',
  pending: 'Not signed yet',
};
export const CONSENT_TYPE_LABELS: Record<ConsentType, string> = {
  principal_consent: 'Consent from the Principal',
  attorney_declaration: 'Declaration by an attorney or deputy',
};

/** UK GDPR Article 6 basis recorded with each consent record (R-5.2 lawful-basis record). */
export const LAWFUL_BASIS_LABELS: Record<string, string> = {
  consent: 'Consent of the person (UK GDPR Article 6(1)(a))',
  attorney_authority:
    'Decision made for the person by their attorney or deputy acting in their best interests (Mental Capacity Act 2005)',
};

/** UK GDPR Article 9 condition recorded for health information (R-5.2). */
export const HEALTH_LAWFUL_BASES = [
  'explicit_consent',
  'lpa_health_and_welfare',
  'deputyship_order',
  'attorney_best_interests',
] as const;
export const HealthLawfulBasis = z.enum(HEALTH_LAWFUL_BASES);
export type HealthLawfulBasis = z.infer<typeof HealthLawfulBasis>;
export const HEALTH_LAWFUL_BASIS_LABELS: Record<HealthLawfulBasis, string> = {
  explicit_consent: 'Explicit consent of the person (UK GDPR Article 9(2)(a))',
  lpa_health_and_welfare: 'Decision by a health and welfare attorney in the person’s best interests',
  deputyship_order: 'Decision by a Court of Protection deputy in the person’s best interests',
  attorney_best_interests:
    'Decision by a property and financial affairs attorney in the person’s best interests (needs legal review)',
};

export const ConsentRecordDto = z.object({
  id: Id,
  type: ConsentType,
  status: z.enum(['pending', 'signed', 'withdrawn']),
  method: ConsentMethod,
  /** Name as signed / declared. */
  signedByName: z.string().nullable(),
  /** The account that recorded it ("the uploading user", R-5.3). */
  recordedByName: z.string().nullable(),
  signedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  lawfulBasis: z.string().nullable(),
  healthDataConsent: z.boolean(),
  healthLawfulBasis: HealthLawfulBasis.nullable(),
  healthConsentAt: IsoDateTime.nullable(),
  healthWithdrawnAt: IsoDateTime.nullable(),
  /** The declaration text exactly as it was shown. */
  statement: z.string().nullable(),
  declarationId: z.string().nullable(),
  declarationVersion: z.number().int().nullable(),
  witnessName: z.string().nullable(),
  hasSignature: z.boolean(),
  document: LinkedDocumentRef.nullable(),
  legalInstrument: z
    .object({ id: Id, type: LegalInstrumentType, registrationDate: IsoDate.nullable() })
    .nullable(),
  withdrawnAt: IsoDateTime.nullable(),
  withdrawnByName: z.string().nullable(),
  withdrawalReason: z.string().nullable(),
});
export type ConsentRecordDto = z.infer<typeof ConsentRecordDto>;

export const ConsentsResponse = z.object({
  records: z.array(ConsentRecordDto),
  summary: z.custom<ConsentSummary>(),
  /** May record or withdraw consent (owner, attorney with LPA, the Principal). */
  canRecord: z.boolean(),
  /** May make the attorney declaration (owner or attorney with an uploaded LPA). */
  canDeclareAsAttorney: z.boolean(),
  /** Health records hidden (and later deleted) because no consent covering health is in force. */
  healthErasure: HealthErasure.nullable(),
});
export type ConsentsResponse = z.infer<typeof ConsentsResponse>;

const PngDataUrl = z
  .string()
  .max(400_000, 'The signature image is too large')
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'The signature must be a PNG image');

export const PrincipalConsentRequest = z
  .object({
    method: z.enum(['in_app_signature', 'paper_upload', 'witnessed_declaration']),
    /** The Principal's name as they typed or signed it. */
    signerName: z.string().trim().min(1, 'Type the name of the person signing').max(120),
    /** Paper and witnessed declarations: the date it was signed (defaults to today). */
    signedOn: IsoDate.optional(),
    /** In-app signature: the drawn signature (PNG data URL). */
    signature: PngDataUrl.optional(),
    witnessName: z.string().trim().max(120).optional(),
    /** Paper declaration photo (required for paper_upload), a vault document. */
    documentId: Id.optional(),
    /** Version of the declaration the person saw (must match the current content). */
    declarationVersion: z.number().int().positive(),
    confirmed: z.literal(true, { error: 'Tick to confirm the declaration has been read and agreed' }),
    /** Explicit, separate consent to recording health information (R-5.2). */
    healthDataConsent: z.boolean(),
    healthDeclarationVersion: z.number().int().positive().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.method === 'in_app_signature' && !v.signature)
      ctx.addIssue({ code: 'custom', path: ['signature'], message: 'Draw a signature in the box' });
    if (v.method === 'paper_upload' && !v.documentId)
      ctx.addIssue({ code: 'custom', path: ['documentId'], message: 'Add a photo or scan of the signed declaration' });
    if (v.method === 'witnessed_declaration' && !v.witnessName)
      ctx.addIssue({ code: 'custom', path: ['witnessName'], message: 'Enter the witness’s name' });
  });
export type PrincipalConsentRequest = z.infer<typeof PrincipalConsentRequest>;

export const AttorneyDeclarationRequest = z.object({
  legalInstrumentId: Id,
  declarantName: z.string().trim().min(1, 'Type your full name').max(120),
  signature: PngDataUrl.optional(),
  declarationVersion: z.number().int().positive(),
  confirmed: z.literal(true, { error: 'Tick to confirm each statement in the declaration' }),
  /** Confirm health information may be recorded in the person's best interests (R-5.2). */
  healthDataConsent: z.boolean(),
});
export type AttorneyDeclarationRequest = z.infer<typeof AttorneyDeclarationRequest>;

export const WithdrawConsentRequest = z.object({
  /** "health": withdraw only the consent to health information. */
  scope: z.enum(['all', 'health']),
  reason: z.string().trim().max(1000).optional(),
});
export type WithdrawConsentRequest = z.infer<typeof WithdrawConsentRequest>;

/** Consent position for cover pages, Home and other modules (`getConsentSummary(ctx)`). */
export interface ConsentSummary {
  status: 'signed' | 'pending' | 'withdrawn' | 'none';
  /** The record that currently applies (latest signed), if any. */
  recordId: string | null;
  type: ConsentType | null;
  method: ConsentMethod | null;
  signedByName: string | null;
  signedAt: string | null;
  lawfulBasis: string | null;
  /** Health information may be recorded (explicit consent or an attorney/deputy decision, not withdrawn). */
  healthDataConsent: boolean;
  healthLawfulBasis: HealthLawfulBasis | null;
  /** One plain-English line for cover pages (FR-1.4). */
  text: string;
}

export const SignatureResponse = z.object({ signature: z.string().nullable() });
export type SignatureResponse = z.infer<typeof SignatureResponse>;

// =================================================================== activity feed (FR-1.6)

/** Audit actions shown in the activity feed ("who added or changed what"). Reads and sign-ins are not. */
export const ACTIVITY_ACTIONS = [
  'create',
  'update',
  'delete',
  'role_change',
  'invite',
  'consent',
  'restore',
  'archive_correction',
  'export',
] as const;

export const ActivityQuery = PaginationQuery.extend({
  module: AppModule.optional(),
  userId: Id.optional(),
});
export type ActivityQuery = z.infer<typeof ActivityQuery>;

export const ActivityItem = z.object({
  id: Id,
  at: IsoDateTime,
  module: z.string(),
  action: z.string(),
  entityType: z.string().nullable(),
  entityId: Id.nullable(),
  /** Never contains amounts or health detail (guarded when written). */
  summary: z.string(),
  actor: z.object({ userId: Id, name: z.string().nullable() }).nullable(),
});
export type ActivityItem = z.infer<typeof ActivityItem>;
export const ActivityList = paginated(ActivityItem);
export type ActivityList = z.infer<typeof ActivityList>;

export const ACTIVITY_MODULE_LABELS: Record<AppModule, string> = {
  workspace: 'Family and profile',
  documents: 'Documents',
  money: 'Money',
  gifts: 'Gifts',
  care: 'Care finances',
  health: 'Health evidence',
  attorney: 'Attorney records',
  platform: 'Settings and reminders',
};

// =================================================================== comments & @mentions (FR-1.7)

/** Visibility module of each commentable record (R-3.3). Comments inherit it, so hidden records' comments are hidden. */
export const ENTITY_VISIBILITY_MODULE: Record<EntityType, VisibilityModule | null> = {
  gift: 'gifts',
  recipient: 'gifts',
  tax_year_summary: 'gifts',
  transaction: 'money',
  account: 'money',
  health_entry: 'health',
  chc_process: 'health',
  benefit_claim: 'health',
  document: 'documents',
  decision: 'attorney',
  attorney_expense: 'attorney',
  care_event: 'care',
  care_invoice: 'care',
  care_contract: 'care',
  principal: null,
  legal_instrument: null,
  consent_record: null,
};

/** "a gift", "a diary entry" — used in activity summaries and notifications (never record content). */
export const ENTITY_TYPE_NOUNS: Record<EntityType, string> = {
  gift: 'a gift',
  recipient: 'a gift recipient',
  tax_year_summary: 'a tax year summary',
  transaction: 'a transaction',
  account: 'an account',
  health_entry: 'a diary entry',
  chc_process: 'the CHC process',
  benefit_claim: 'a benefit claim',
  document: 'a document',
  decision: 'a decision',
  attorney_expense: 'an attorney expense',
  care_event: 'a care event',
  care_invoice: 'a care invoice',
  care_contract: 'a care contract',
  principal: 'the Principal profile',
  legal_instrument: 'a power of attorney record',
  consent_record: 'a consent record',
};

export const CommentQuery = z.object({ entityType: EntityType, entityId: Id });
export type CommentQuery = z.infer<typeof CommentQuery>;

export const CommentPerson = z.object({ userId: Id, name: z.string().nullable() });
export type CommentPerson = z.infer<typeof CommentPerson>;

export const CommentDto = z.object({
  id: Id,
  entityType: EntityType,
  entityId: Id,
  /** Text with mention tokens `@[Name](userId)` (see `splitMentions`). */
  body: z.string(),
  author: CommentPerson.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  edited: z.boolean(),
  mine: z.boolean(),
  mentions: z.array(CommentPerson),
});
export type CommentDto = z.infer<typeof CommentDto>;

export const CommentList = z.object({ items: z.array(CommentDto), total: z.number().int() });
export type CommentList = z.infer<typeof CommentList>;

export const COMMENT_MAX_LENGTH = 4000;

export const CreateCommentRequest = z.object({
  entityType: EntityType,
  entityId: Id,
  body: z.string().trim().min(1, 'Write a comment').max(COMMENT_MAX_LENGTH, 'Keep comments under 4,000 characters'),
  /** In-app path of the record, used as the link in mention notifications (must be inside this workspace). */
  link: z.string().max(500).optional(),
});
export type CreateCommentRequest = z.infer<typeof CreateCommentRequest>;

export const UpdateCommentRequest = z.object({
  body: z.string().trim().min(1, 'Write a comment').max(COMMENT_MAX_LENGTH, 'Keep comments under 4,000 characters'),
  link: z.string().max(500).optional(),
});
export type UpdateCommentRequest = z.infer<typeof UpdateCommentRequest>;

export const CommentCountsQuery = z.object({
  entityType: EntityType,
  /** Comma-separated record ids (up to 200). */
  ids: z
    .string()
    .max(200 * 37)
    .transform((s) => s.split(',').map((x) => x.trim()).filter(Boolean))
    .pipe(z.array(Id).max(200)),
});
export const CommentCounts = z.object({ counts: z.record(z.string(), z.number().int()) });
export type CommentCounts = z.infer<typeof CommentCounts>;

export const MentionableQuery = z.object({
  entityType: EntityType,
  /** The record being commented on: documents are visible per document (SR-27), so who can be mentioned depends
   *  on the document itself. */
  entityId: Id.optional(),
});
export const MentionableMember = z.object({ userId: Id, name: z.string(), role: Role });
export type MentionableMember = z.infer<typeof MentionableMember>;
export const MentionableList = z.object({ items: z.array(MentionableMember) });
export type MentionableList = z.infer<typeof MentionableList>;

/** `@[Sarah Hughes](8a0c…)` — the stored form of an @mention. */
export const MENTION_PATTERN = /@\[([^\]\n]{1,120})\]\(([0-9a-fA-F-]{36})\)/g;

/** Mentioned user ids in a comment body (unique, in order). */
export function parseMentionIds(body: string): string[] {
  const out: string[] = [];
  for (const m of body.matchAll(new RegExp(MENTION_PATTERN.source, 'g'))) {
    const id = (m[2] ?? '').toLowerCase();
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

export type MentionSegment = { kind: 'text'; text: string } | { kind: 'mention'; name: string; userId: string };

/** Splits a comment body into plain text and mention segments for rendering. */
export function splitMentions(body: string): MentionSegment[] {
  const out: MentionSegment[] = [];
  let last = 0;
  for (const m of body.matchAll(new RegExp(MENTION_PATTERN.source, 'g'))) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: 'text', text: body.slice(last, at) });
    out.push({ kind: 'mention', name: m[1] ?? '', userId: (m[2] ?? '').toLowerCase() });
    last = at + m[0].length;
  }
  if (last < body.length) out.push({ kind: 'text', text: body.slice(last) });
  return out;
}

/** Plain text of a comment with mentions shown as "@Name". */
export function commentPlainText(body: string): string {
  return splitMentions(body)
    .map((s) => (s.kind === 'text' ? s.text : `@${s.name}`))
    .join('');
}

/** Builds the stored token for a mention (names cannot contain brackets or new lines). */
export function mentionToken(name: string, userId: string): string {
  const safe = name.replace(/[[\]\n\r]/g, ' ').trim().slice(0, 120) || 'member';
  return `@[${safe}](${userId})`;
}
